// src/components/ParticipantsByActivityType.jsx
//
// Ladder bar chart showing participant counts (receptive + active) per
// activity type, broken into Developmental and Presentational groups.
// Each group has its own scale and an independent "Hide unused" checkbox.
//
// Ported from takso-poc ParticipantsByActivityTypeChart (registry.jsx).
// Inline styles replaced with BEM SCSS classes (.pax-*) in charts.scss.
//
// Data shape (from /report-builder/charts/participant-counts-by-activity-type):
//   groups: [
//     { group: 'Developmental',  eventTypeId: 4, total: N,
//       types: [{ typeId, name, receptive, active, total }] },
//     { group: 'Presentational', eventTypeId: 3, total: N,
//       types: [{ typeId, name, receptive, active, total }] },
//   ]

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocaleService } from '../services';

import '../charts/charts.scss';

const BAR_COLOR = '#D92B2C';
const MAX_BAR_PCT = 93;

const fmtN = (n) => LocaleService.fmtLocaleNumeral(Number(n) || 0);

function buildProse(t, groups) {
  const usedGroups = groups.filter((g) => g.total > 0);
  if (usedGroups.length === 0) return null;

  const total = usedGroups.reduce((s, g) => s + g.total, 0);
  const topGroup = usedGroups[0];

  // Sentence 1 — total and top group
  const s1 =
    usedGroups.length === 1
      ? t('charts.participants_by_type.data_by_activity_type_shows', {
          num: fmtN(total),
          group: topGroup.group,
        })
      : t('charts.participants_by_type.data_by_activity_type_with_most', {
          num: fmtN(total),
          numGroups: usedGroups.length,
          group: topGroup.group,
          topGroupNum: fmtN(topGroup.total),
        });

  // Sentence 2 — top individual type across all groups
  const allTypes = groups.flatMap((g) => g.types);
  const topType = allTypes
    .filter((t) => t.total > 0)
    .sort((a, b) => b.total - a.total)[0];
  const s2 = topType
    ? t('charts.participants_by_type.most_common_activity_type', {
        name: topType.name,
        num: fmtN(topType.total),
      })
    : null;

  return [s1, s2].filter(Boolean).join(' ');
}

export function ParticipantsByActivityType({ data }) {
  const { t } = useTranslation();
  const groups = Array.isArray(data?.groups) ? data.groups : [];

  // Independent hide-unused toggle per group, keyed by group name
  const [hideUnused, setHideUnused] = useState({});

  const prose = buildProse(t, groups);

  if (groups.length === 0) {
    return (
      <div className="chart-empty chart-empty--centered">
        {t('charts.participants_by_type.no_participant_data_by_activity_type')}
      </div>
    );
  }

  return (
    <div>
      {groups.map((group) => {
        const isHidden = !!hideUnused[group.group];
        const allSorted = [...group.types].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        const visible = isHidden
          ? allSorted.filter((t) => t.total > 0)
          : allSorted;
        const hasData = allSorted.some((t) => t.total > 0);
        const groupMax = Math.max(...allSorted.map((t) => t.total), 1);

        return (
          <div key={group.group} className="pax-group">
            {/* Group header */}
            <div className="pax-group__header">
              <span className="pax-group__name">{group.group}</span>
              <span className="pax-group__total">
                {t('charts.participants_by_type.num_participants', {
                  count: group.total,
                  countFmt: fmtN(group.total),
                })}
              </span>
              <label
                className="pax-group__hide-unused"
                data-html2canvas-ignore="true"
              >
                <input
                  type="checkbox"
                  checked={isHidden}
                  onChange={(e) =>
                    setHideUnused((prev) => ({
                      ...prev,
                      [group.group]: e.target.checked,
                    }))
                  }
                />
                {t('charts.participants_by_type.hide_unused')}
              </label>
            </div>

            {/* Type rows */}
            {!hasData ? (
              <div className="chart-empty">
                {t('charts.participants_by_type.no_data_for_this_group')}
              </div>
            ) : visible.length === 0 ? (
              <div className="chart-empty">
                {t('charts.participants_by_type.all_types_hidden')}
              </div>
            ) : (
              <div className="chart-bar-list">
                {visible.map((t) => {
                  const isTop = t.total > 0 && t.total === groupMax;
                  const pct =
                    t.total > 0 ? (t.total / groupMax) * MAX_BAR_PCT : 0;

                  return (
                    <div key={t.typeId} className="chart-bar-row">
                      <div
                        className={`chart-bar-row__label${
                          t.total > 0 ? '' : ' chart-bar-row__label--unused'
                        }`}
                      >
                        {t.name}
                      </div>
                      <div className="chart-bar-row__bar-area">
                        {t.total > 0 ? (
                          <>
                            <div
                              className={`chart-bar-row__bar${
                                isTop ? ' chart-bar-row__bar--top' : ''
                              }`}
                              style={{
                                background: BAR_COLOR,
                                ...(!isTop && { width: `${pct}%` }),
                              }}
                            />
                            <span className="chart-bar-row__count">
                              {fmtN(t.total)}
                            </span>
                          </>
                        ) : (
                          <div className="chart-bar-row__bar-spacer" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {prose && <p className="chart-prose">{prose}</p>}
    </div>
  );
}
